/**
 * Template detail screen (P1-T13, redesigned 2026-10-17) — `#/template/:slug`.
 *
 * Layout (design "Fantasy Butterfly"): full-bleed preview with back / share
 * buttons (▶ badge on video templates) → title (last word in gradient) →
 * description → tag chips → 4 info items → "Similar Templates" rail → sticky
 * "Try This Template" CTA that opens the editor (#/editor/:tool?template=).
 *
 * Everything shown is real data: chips come from the media kind, category and
 * badges; info items from editorSchema / qualityTiers / creditCost. There are
 * no use counts, likes or bookmarks (no backend for them), and no duration
 * (not a template field). Quality tiers are chosen in the editor.
 *
 * States: loading skeleton, not-found (bad slug → 404), API error with retry.
 * Deep-linkable. Back → history.back() with a `#/home` fallback.
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { bindCompareSliders, compareSliderHtml } from '../components/compareSlider.js';
import { beforeInsetHtml } from '../components/beforeInset.js';
import { inputLabels, twoPhotoExampleHtml } from '../components/twoPhotoExample.js';
import { toast, toastApiError } from '../ui/toast.js';
import {
  bindCardImages,
  templateCardHtml,
  templateCardSkeletonHtml,
} from '../components/templateCard.js';
import type { Category, CreditPricingConfig, Template } from '@loomia/shared';

/** Incremented on every render; async loaders bail out when stale. */
let renderSeq = 0;

function goBack(): void {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    navigate('/home');
  }
}

async function share(t: Template): Promise<void> {
  const url = window.location.href;
  try {
    if (navigator.share) {
      await navigator.share({ title: t.title, text: t.description || t.title, url });
      return;
    }
    await navigator.clipboard.writeText(url);
    toast('Link copied.');
  } catch (e) {
    // Dismissing the native share sheet rejects with AbortError: not an error.
    if ((e as DOMException)?.name !== 'AbortError') toast('Couldn’t share this template.');
  }
}

function skeletonHtml(): string {
  return `
    <div class="tpld" aria-busy="true" aria-label="Loading template">
      <div class="tpld-hero"><span class="shimmer" aria-hidden="true"></span></div>
      <div class="stack tpld-body">
        <span class="shimmer-line" style="width: 62%; height: 22px;"></span>
        <span class="shimmer-line"></span>
        <span class="shimmer-line" style="width: 72%;"></span>
        <div class="tpld-chips" aria-hidden="true">
          <span class="chip"><span class="shimmer-line short"></span></span>
          <span class="chip"><span class="shimmer-line short"></span></span>
        </div>
      </div>
    </div>`;
}

function notFoundHtml(): string {
  return `
    <div class="stack">
      <div class="empty" style="padding-top: 72px;">
        <div class="empty-icon">${icon('search')}</div>
        <h1 class="page-title">Template not found</h1>
        <p class="muted" style="max-width: 260px;">This template may have been removed or the link is wrong.</p>
        <div style="display: flex; gap: var(--sp-2); width: 100%; max-width: 320px;">
          <button class="btn btn-secondary" style="flex: 1;" data-action="back">Go back</button>
          <a class="btn btn-primary" style="flex: 1;" href="#/home">Home</a>
        </div>
      </div>
    </div>`;
}

function errorHtml(): string {
  return `
    <div class="stack">
      <div class="empty" style="padding-top: 72px;">
        <div class="empty-icon">${icon('cloud_off')}</div>
        <p class="muted" style="max-width: 260px;">Couldn't load this template. Check your connection and try again.</p>
        <button class="btn btn-secondary" data-action="retry">Try again</button>
      </div>
    </div>`;
}

/** `#/editor/<tool>?template=<slug>` — the P1-T14 handoff. */
function editorHref(t: Template): string {
  return `#/editor/${encodeURIComponent(t.tool)}?template=${encodeURIComponent(t.slug)}`;
}

function inputCount(t: Template): number {
  const inputs = (t.editorSchema as { inputs?: unknown }).inputs;
  return Array.isArray(inputs) && inputs.length > 0 ? inputs.length : 1;
}

/** Title with its last word in the brand gradient. */
function titleHtml(title: string): string {
  const words = title.trim().split(/\s+/);
  if (words.length < 2) return `<span class="tpld-grad">${escapeHtml(title)}</span>`;
  const last = words.pop()!;
  return `${escapeHtml(words.join(' '))} <span class="tpld-grad">${escapeHtml(last)}</span>`;
}

function chipsHtml(t: Template, category: Category | null): string {
  const video = t.mediaKind === 'video';
  const chips = [
    `<span class="tpld-chip is-media">${icon(video ? 'play_arrow' : 'image', { fill: true })}${video ? 'AI Video' : 'AI Photo'}</span>`,
    ...(category ? [`<span class="tpld-chip">${escapeHtml(category.title)}</span>`] : []),
    ...(t.badges.includes('new') || t.isNew ? ['<span class="tpld-chip is-new">New</span>'] : []),
    ...(t.badges.includes('hot') ? ['<span class="tpld-chip is-hot">Popular</span>'] : []),
  ];
  return chips.join('');
}

/** Cheapest and longest clip the tool offers, from live pricing (null = not duration-priced). */
function durationInfo(t: Template, pricing: CreditPricingConfig | null): { range: string; from: number } | null {
  const entry = pricing?.perTool[t.tool];
  if (!entry?.perTierDuration) return null;
  const durs = entry.durations && entry.durations.length > 0 ? entry.durations : [5, 10, 15];
  const tiers = (t.qualityTiers && t.qualityTiers.length > 0 ? t.qualityTiers : (Object.keys(entry.perTierDuration) as never[]));
  const shortest = Math.min(...durs);
  const prices = tiers
    .map((q) => entry.perTierDuration?.[q]?.[shortest as 5])
    .filter((v): v is number => typeof v === 'number');
  if (prices.length === 0) return null;
  const longest = Math.max(...durs);
  return { range: shortest === longest ? `${shortest}s` : `${shortest}–${longest}s`, from: Math.min(...prices) };
}

function infoHtml(t: Template, pricing: CreditPricingConfig | null = null): string {
  const n = inputCount(t);
  const tiers = t.qualityTiers ?? [];
  const best = tiers.length > 0 ? tiers[tiers.length - 1]!.toUpperCase() : 'Standard';
  const dur = t.mediaKind === 'video' ? durationInfo(t, pricing) : null;
  const items = [
    dur
      ? { ic: 'timer', v: dur.range, l: 'Duration' }
      : { ic: t.mediaKind === 'video' ? 'movie' : 'image', v: t.mediaKind === 'video' ? 'Video' : 'Photo', l: 'Output' },
    { ic: 'photo_camera', v: `${n} Photo${n > 1 ? 's' : ''}`, l: 'Input' },
    { ic: 'hd', v: best, l: tiers.length > 1 ? 'Up to' : 'Quality' },
    dur
      ? { ic: 'toll', v: `${dur.from}+`, l: 'Credits' }
      : { ic: 'toll', v: String(t.creditCost), l: 'Credits' },
  ];
  return items
    .map((i) => `<li>${icon(i.ic, { fill: true })}<b>${escapeHtml(i.v)}</b><i>${escapeHtml(i.l)}</i></li>`)
    .join('');
}

function detailHtml(t: Template, category: Category | null): string {
  const video = t.mediaKind === 'video';
  const clip = video ? t.previewVideoUrl : undefined;
  return `
    <div class="tpld">
      <div class="tpld-hero${video && !clip ? '' : ' is-photo'}">
        ${
          clip
            ? `<video class="tpld-clip" src="${escapeHtml(clip)}" poster="${escapeHtml(t.previewUrl || t.thumbnailUrl)}"
                autoplay muted loop playsinline aria-label="${escapeHtml(t.title)} example video"></video>${
                t.beforeUrl ? beforeInsetHtml(t.beforeUrl, 'tpld-bfi') : ''
              }`
            : !video && t.beforeUrl && t.secondBeforeUrl
            ? twoPhotoExampleHtml({
                inputs: [
                  { url: t.beforeUrl, label: inputLabels(t.editorSchema)[0] ?? 'Your photo' },
                  { url: t.secondBeforeUrl, label: inputLabels(t.editorSchema)[1] ?? 'Second photo' },
                ],
                result: t.previewUrl || t.thumbnailUrl,
                className: 'tpld-tpx',
              })
            : !video && t.beforeUrl
            ? compareSliderHtml({
                before: t.beforeUrl,
                after: t.previewUrl || t.thumbnailUrl,
                ratio: '3 / 4',
                className: 'tpld-cmp',
                ariaLabel: 'Compare before and after',
              })
            : `<span class="shimmer" aria-hidden="true"></span>
        <img
          src="${escapeHtml(t.previewUrl || t.thumbnailUrl)}"
          alt="${escapeHtml(t.title)}"
          data-tpl-img
          decoding="async"
          fetchpriority="high"
        />`
        }
        <button class="tpld-round tpld-back" data-action="back" aria-label="Back">${icon('arrow_back')}</button>
        <button class="tpld-round tpld-share" data-action="share" aria-label="Share">${icon('ios_share')}</button>
        ${video && !clip ? `<span class="tpld-play" aria-label="Video template">${icon('play_arrow', { fill: true })}</span>` : ''}
      </div>

      <div class="stack tpld-body">
        <h1 class="tpld-title">${titleHtml(t.title)}</h1>
        ${t.description ? `<p class="tpld-desc">${escapeHtml(t.description)}</p>` : ''}
        <div class="tpld-chips" id="tpld-chips">${chipsHtml(t, category)}</div>
        <ul class="tpld-info" aria-label="Details">${infoHtml(t)}</ul>

        <section aria-label="Similar templates" id="tpld-related" hidden>
          <div class="rail-head">
            <h2 class="section-title" style="margin: 0;">Similar Templates</h2>
            <a class="see-all" href="#/explore">See all${icon('chevron_right')}</a>
          </div>
          <div id="tpld-related-rail"><div class="rail" aria-label="Loading similar templates">${templateCardSkeletonHtml(4)}</div></div>
        </section>
      </div>

      <div class="tpld-cta">
        <a class="tpld-try" id="tpld-try" href="${editorHref(t)}">
          <span>Try This Template</span>${icon('auto_awesome', { fill: true })}
        </a>
      </div>
    </div>`;
}

export function renderTemplateDetail(root: HTMLElement, slug: string): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;
  let current: Template | null = null;

  const bindActions = (): void => {
    root.querySelectorAll<HTMLElement>('[data-action]').forEach((el) => {
      const action = el.dataset.action;
      if (action === 'back') el.addEventListener('click', goBack);
      else if (action === 'retry') el.addEventListener('click', () => void load());
      else if (action === 'share') el.addEventListener('click', () => current && void share(current));
    });
  };

  const load = async (): Promise<void> => {
    if (!slug) {
      if (alive()) {
        root.innerHTML = notFoundHtml();
        bindActions();
      }
      return;
    }
    if (alive()) root.innerHTML = skeletonHtml();

    let t: Template;
    try {
      t = await api.getTemplate(slug);
      api.recordTemplateView(slug);
    } catch (err) {
      if (!alive()) return;
      if (err instanceof ApiError && err.status === 404) {
        root.innerHTML = notFoundHtml();
      } else {
        root.innerHTML = errorHtml();
        toastApiError(err, "Couldn't load this template.");
      }
      bindActions();
      return;
    }
    if (!alive()) return;
    current = t;

    root.innerHTML = detailHtml(t, null);
    bindActions();
    bindCompareSliders(root);
    bindCardImages(root);

    // Live pricing: duration range + "from" price for video. Non-critical.
    if (t.mediaKind === 'video') {
      void api
        .getPricing()
        .then((pricing) => {
          if (!alive()) return;
          const list = root.querySelector<HTMLElement>('.tpld-info');
          if (list) list.innerHTML = infoHtml(t, pricing);
        })
        .catch(() => undefined);
    }

    // Category chip: non-critical, added once the categories arrive.
    void api
      .listCategories()
      .then((cats) => {
        if (!alive()) return;
        const cat = cats.find((c) => c.id === t.categoryId) ?? null;
        const chips = root.querySelector<HTMLElement>('#tpld-chips');
        if (chips && cat) chips.innerHTML = chipsHtml(t, cat);
      })
      .catch(() => undefined);

    // Similar rail: same category, exclude self. Non-critical — hide on failure.
    const relatedSection = root.querySelector<HTMLElement>('#tpld-related');
    const relatedRail = root.querySelector<HTMLElement>('#tpld-related-rail');
    if (relatedSection && relatedRail) {
      relatedSection.hidden = false;
      try {
        const page = await api.listTemplates({ category: t.categoryId, limit: 13 });
        if (!alive()) return;
        const related = page.templates.filter((r) => r.slug !== t.slug).slice(0, 12);
        if (related.length === 0) {
          relatedSection.hidden = true;
          return;
        }
        relatedRail.innerHTML = `<div class="rail" role="list" aria-label="Similar templates">${related
          .map((r) => templateCardHtml(r))
          .join('')}</div>`;
        bindCardImages(relatedRail);
      } catch {
        if (alive()) relatedSection.hidden = true;
      }
    }
  };

  void load();
}
